/**
 * SNS Post Handler
 *
 * Post-level SNS glue.
 * Event handler layer: handle{Action}(event)
 */

import { SNSPostRender } from "./sns-post-render";
import { snsPostForm } from "./sns-post-form";

type SNSPostHandlerParams = {
    getSnsLinksList: () => HTMLElement | null | undefined;
    updatePreviews: () => void;
    inputDebounceMs?: number;
    deps?: { render?: typeof SNSPostRender };
};

export class SNSPostHandler {
    private readonly getSnsLinksList: () => HTMLElement | null | undefined;
    private readonly updatePreviews: () => void;
    private readonly inputDebounceMs: number;
    private readonly render: typeof SNSPostRender;
    private _debounceTimer: ReturnType<typeof setTimeout> | null;

    /** Initialize the post-level SNS handler. */
    public constructor(params: SNSPostHandlerParams) {
        const { getSnsLinksList, updatePreviews, inputDebounceMs = 500, deps = {} } = params;

        this.getSnsLinksList = getSnsLinksList;
        this.updatePreviews = updatePreviews;
        this.inputDebounceMs = inputDebounceMs;

        this.render = deps.render || SNSPostRender;

        this._debounceTimer = null;
    }

    /**
     * Add a new sns_links[] row.
     * Called by SNS links panel external handler hook.
     */
    public handleSnsAdd(_event?: Event): void {
        const snsLinksList = this.getSnsLinksList?.() || null;
        if (!snsLinksList) return;

        this.render.appendSnsLinkItem({ snsLinksList, value: "", focus: true });
    }

    /** Debounce preview updates while the user edits SNS links. */
    public handleSnsInput(event: Event): void {
        const target = event?.target;
        if (!(target instanceof Element)) return;

        const input = snsPostForm.getClosestLinkInput(target);
        if (!input) return;

        if (this._debounceTimer) {
            clearTimeout(this._debounceTimer);
        }

        this._debounceTimer = setTimeout(() => {
            this.updatePreviews();
        }, this.inputDebounceMs);
    }

    /** Activate an image-first SNS tile into an interactive embed. */
    public handleSnsPreviewActivate(event: Event): void {
        const target = event?.target;
        if (!(target instanceof Element)) return;

        if (snsPostForm.getClosestRemoveButton(target)) {
            return;
        }

        const tile = snsPostForm.getClosestPreviewTile(target);
        if (!(tile instanceof HTMLElement)) {
            return;
        }

        if (String(tile.dataset.previewState || "").trim() !== "image") {
            return;
        }

        event.preventDefault();
        this.render.activateSnsPreviewTile(tile);
    }
}
